import { Component, Input, OnInit } from '@angular/core';
import { api_routes, db_tables } from '@app/consts';
import { ArticlesService } from '../../services/articles.service';
import { EmployeeArticle } from '../../models/article.model';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ArticleDialogComponent } from '../article-dialog/article-dialog.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-articles-display',
  templateUrl: './articles-display.component.html',
  styleUrls: ['./articles-display.component.scss']
})
export class ArticlesDisplayComponent implements OnInit {
  @Input() region: string;
  @Input() title: string;

  public api_routes = api_routes;
  public db_tables = db_tables;
  isLoading: boolean = true;
  pageSize: number = 10;
  skip: number = 0;
  articles: EmployeeArticle[] = [];
  dialogRef: any;
  currentIndex = 0;
  cardsPerView = 3; // Number of cards visible at once
  cardWidth = 583; // 500px card + 16px gap
  translateX = 0;
  total: number;
  articleImageUrls: Map<string, any> = new Map();
  clickedArticleIds: Set<string> = new Set();
  isTitleTruncated: boolean = false;

  constructor(
    private articlesService: ArticlesService,
    public translate: TranslateService,
    private dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this.getArticles();
  }

  getArticles() {
    this.isLoading = true;

    this.articlesService.getEmployeeArticles(this.region, this.pageSize, this.skip, null, null)
      .subscribe(
        {
          next: (v) => {
            this.articles = this.articles.concat(v.data);
            this.total = v.total;
            this.cacheArticleImageUrls(v.data);
            this.isLoading = false;
          },
          error: (e) => {
            this.isLoading = false;
          }
        }
      );
  }
  
  loadMoreArticles() {
    this.skip += this.pageSize;
    this.getArticles();
  }

  openArticleDialog(article: EmployeeArticle) {
    this.clickedArticleIds.add(article.id);
    
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      article: article
    };

    this.dialogRef = this.dialog.open(ArticleDialogComponent, dialogConfig);

    const sub = this.dialogRef.componentInstance.emitFormData.subscribe((event) => {
      sub.unsubscribe();
    });
  }

  cacheArticleImageUrls(articles: EmployeeArticle[]): void {
    articles.forEach(article => {
      if (article.titleImage && !this.articleImageUrls.has(article.id)) {
        const imageUrl = this.articlesService.getEmployeeArticleFile(article.id, article.titleImage.id);
        this.articleImageUrls.set(article.id, imageUrl);
      }
    });
  }

  getArticleImageUrl(article: EmployeeArticle): any {
    return this.articleImageUrls.get(article.id) || null;
  }

  get maxIndex(): number {
    return Math.max(0, this.articles.length - this.cardsPerView);
  }

  nextCards(): void {
    if (this.currentIndex < this.maxIndex) {
      this.currentIndex++;
      this.updateTranslateX();

      if (this.currentIndex >= this.articles.length - this.cardsPerView && this.articles.length < this.total) {
        this.loadMoreArticles();
      }
    }
  }

  previousCards(): void {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updateTranslateX();
    }
  }

  private updateTranslateX(): void {
    this.translateX = -this.currentIndex * this.cardWidth;
  }

  isArticleRead(article: EmployeeArticle): boolean {
    return (article.lastViewedOn !== null && article.lastViewedOn >= article.lastEditedOn) || this.clickedArticleIds.has(article.id);
  }

  shouldShowTooltip(event: MouseEvent): boolean {
    const element = event.target as HTMLElement;
    return element.scrollWidth > element.clientWidth;
  }

}
